import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { COMPANY, SERVICES, AREAS } from "@/data/site";

const DropMenu = ({ label, items, base, testid }) => (
  <div className="group relative">
    <button
      data-testid={testid}
      className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-navy transition-colors hover:text-salmon"
    >
      {label}
      <ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
    </button>
    <div className="invisible absolute left-0 top-full z-50 w-64 translate-y-2 rounded-xl border border-slate-200 bg-white p-2 opacity-0 shadow-xl transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
      {items.map((item) => (
        <Link
          key={item.slug}
          to={`${base}/${item.slug}`}
          data-testid={`${testid}-${item.slug}`}
          className="block rounded-lg px-4 py-2.5 text-sm font-medium text-navy transition-colors hover:bg-salmon-light hover:text-salmon-dark"
        >
          {item.name}
        </Link>
      ))}
    </div>
  </div>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const close = () => setOpen(false);

  const navClass = ({ isActive }) =>
    `px-3 py-2 text-sm font-semibold transition-colors hover:text-salmon ${
      isActive ? "text-salmon" : "text-navy"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 shadow-sm backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" onClick={close} data-testid="nav-link-home" aria-label="Gregory Tile home">
          <img src="/assets/logo.png" alt="Gregory Tile" className="h-16 w-auto" />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          <DropMenu label="Services" items={SERVICES} base="" testid="nav-dropdown-services" />
          <DropMenu label="Service Areas" items={AREAS} base="/service-areas" testid="nav-dropdown-areas" />
          <NavLink to="/contact" className={navClass} data-testid="nav-link-contact">
            Contact
          </NavLink>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={COMPANY.phoneHref}
            data-testid="header-phone-button"
            className="flex items-center gap-2 rounded-full border-2 border-navy px-4 py-2 text-sm font-bold text-navy transition-colors hover:bg-navy hover:text-white"
          >
            <Phone className="h-4 w-4" />
            {COMPANY.phone}
          </a>
          <Link
            to="/contact"
            data-testid="header-estimate-button"
            className="rounded-full bg-salmon px-5 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:bg-salmon-dark hover:shadow-lg"
          >
            Get Free Estimate
          </Link>
        </div>

        <button
          className="rounded-lg p-2 text-navy lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white px-4 py-4 lg:hidden" data-testid="mobile-menu">
          <p className="px-2 pb-1 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-salmon">Services</p>
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to={`/${s.slug}`}
              onClick={close}
              data-testid={`mobile-nav-${s.slug}`}
              className="block rounded-lg px-2 py-2.5 text-sm font-semibold text-navy hover:bg-salmon-light"
            >
              {s.name}
            </Link>
          ))}
          <p className="px-2 pb-1 pt-3 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-salmon">Service Areas</p>
          {AREAS.map((a) => (
            <Link
              key={a.slug}
              to={`/service-areas/${a.slug}`}
              onClick={close}
              data-testid={`mobile-nav-area-${a.slug}`}
              className="block rounded-lg px-2 py-2.5 text-sm font-semibold text-navy hover:bg-salmon-light"
            >
              {a.name}, AL
            </Link>
          ))}
          <Link
            to="/contact"
            onClick={close}
            data-testid="mobile-nav-contact"
            className="block rounded-lg px-2 py-2.5 text-sm font-semibold text-navy hover:bg-salmon-light"
          >
            Contact
          </Link>
        </div>
      )}
    </header>
  );
};

export default Header;
